import { useEffect, useRef, useState } from 'react'
import { Button, Input, Modal, List, Avatar } from 'antd'
import { buildContentWithMentions } from '../../utils/commentParser'
import userService from '../../services/userService'
import { useEmbeddedAuth } from '../../contexts/EmbeddedAuthContext'
import { getCurrentUser } from '../../services/authService'

/**
 * 评论输入框组件，支持 @ 提及
 * @param {{
 *  open: boolean,
 *  onClose?: ()=>void,
 *  onSubmit?: (payload:{content:string,mentions?:Array})=>void,
 *  placeholder?: string,
 *  defaultReplyUser?: string
 * }} props
 */
export default function CommentInput({ open, onClose, onSubmit, placeholder = '写下你的评论...', defaultReplyUser, inline = false, inputRef: externalInputRef }) {
  const [value, setValue] = useState('')
  const [mentionTags, setMentionTags] = useState([])
  const [cursor, setCursor] = useState(0)
  const [showMentionList, setShowMentionList] = useState(false)
  const [mentionKeyword, setMentionKeyword] = useState('')
  const [mentionCandidates, setMentionCandidates] = useState([])
  const inputRef = externalInputRef || useRef()
  const { showLoginModal } = useEmbeddedAuth()
  const currentUser = getCurrentUser()

  /**
   * 文本变化时识别最近一次 '@' 并决定是否展示候选列表
   */
  const handleChange = (e) => {
    const v = e.target.value
    const position = e.target.selectionStart || 0
    setValue(v)
    setCursor(position)
    const lastAtIndex = v.lastIndexOf('@', position)
    if (lastAtIndex !== -1) {
      const textAfterAt = v.substring(lastAtIndex + 1, position)
      if (textAfterAt && !textAfterAt.includes(' ')) {
        setMentionKeyword(textAfterAt)
        setShowMentionList(true)
      } else {
        setShowMentionList(false)
      }
    } else {
      setShowMentionList(false)
    }
  }

  /**
   * 根据关键字拉取 @ 候选用户
   */
  useEffect(() => {
    let active = true
    const fetch = async () => {
      if (!showMentionList || !mentionKeyword) return
      try {
        const res = await userService.searchUsers({ name: mentionKeyword, limit: 8 })
        const users = res?.users || res || []
        if (active) setMentionCandidates(users)
      } catch {
        if (active) setMentionCandidates([])
      }
    }
    fetch()
    return () => { active = false }
  }, [showMentionList, mentionKeyword])

  /**
   * 提交评论内容并将标签转换为占位符
   */
  const handleSubmit = () => {
    if (!currentUser) {
      showLoginModal()
      return
    }
    const { content, mentions } = buildContentWithMentions(value, mentionTags)
    onSubmit?.({ content, mentions })
    setValue('')
    setMentionTags([])
    setShowMentionList(false)
  }

  /**
   * 选择用户插入到文本中，并记录标签位置用于后续占位符替换
   */
  const handleSelectUser = (user) => {
    const v = value
    const lastAtIndex = v.lastIndexOf('@', cursor)
    if (lastAtIndex === -1) return
    const beforeAt = v.substring(0, lastAtIndex)
    const afterCursor = v.substring(cursor)
    const displayText = `@${user.name}`
    const newValue = `${beforeAt}${displayText} ${afterCursor}`
    const tagStart = lastAtIndex
    const tagEnd = lastAtIndex + displayText.length
    setValue(newValue)
    setMentionTags((prev) => (prev.length >= 20 ? prev : [...prev, { start: tagStart, end: tagEnd, user_id: user.id, name: user.name }]))
    setCursor(tagEnd + 1)
    setShowMentionList(false)
    inputRef.current?.focus()
  }

    if (inline) {
      return (
        <div>
          <Input.TextArea ref={inputRef} value={value} onChange={handleChange} placeholder={placeholder} autoSize={{ minRows: 3, maxRows: 6 }} maxLength={1000} />
          {showMentionList && mentionCandidates.length > 0 && (
            <div style={{ border: '1px solid var(--color-border-secondary)', borderRadius: 6, marginTop: 8, maxHeight: 200, overflowY: 'auto' }}>
              <List
                size="small"
                dataSource={mentionCandidates}
                renderItem={(item) => (
                  <List.Item style={{ cursor: 'pointer' }} onClick={() => handleSelectUser(item)}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Avatar size={20} src={item.avatar}>{item.name?.charAt(0)}</Avatar>
                      <span>{item.name}</span>
                    </span>
                  </List.Item>
                )}
              />
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
            <Button type="primary" style={{ color: 'var(--color-text-on-light-base)' }} onClick={handleSubmit}>发送</Button>
          </div>
        </div>
      )
    }

  return (
    <Modal open={open} onCancel={onClose} footer={null} title={defaultReplyUser ? `回复 @${defaultReplyUser}` : '发表评论'}>
      <Input.TextArea ref={inputRef} value={value} onChange={handleChange} placeholder={placeholder} autoSize={{ minRows: 3, maxRows: 6 }} maxLength={1000} />
      {showMentionList && mentionCandidates.length > 0 && (
        <div style={{ border: '1px solid var(--color-border-secondary)', borderRadius: 6, marginTop: 8, maxHeight: 200, overflowY: 'auto' }}>
          <List
            size="small"
            dataSource={mentionCandidates}
            renderItem={(item) => (
              <List.Item style={{ cursor: 'pointer' }} onClick={() => handleSelectUser(item)}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Avatar size={20} src={item.avatar}>{item.name?.charAt(0)}</Avatar>
                  <span>{item.name}</span>
                </span>
              </List.Item>
            )}
          />
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
        <Button type="primary" style={{ color: 'var(--color-text-on-light-base)' }} onClick={handleSubmit}>发送</Button>
      </div>
    </Modal>
  )
}
